import type { GetToken } from "../../../controllers/eqmsApi";
import { lazy, Suspense, useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import {
  actOnReview,
  downloadRevision,
  fetchRevisionBytes,
  friendlyMessage,
  generateDocAiSummary,
  translateDocField,
  getDocument,
  listRevisions,
  listReviewTasks,
  listVersions,
  obsoleteDocument,
  releaseDocument,
  requestReview,
  startDocumentRevision,
  uploadRevisionFile,
} from "../../../controllers/eqmsApi";
import type { DocStatus, Document, DocumentRevision, DocReviewTask, Version } from "../../../models/eqms";
import { DocPreviewModal } from "../../components/DocPreviewModal";
import { FileDrop } from "../../components/FileDrop";
import { DOC_STATUS_LABEL, docAuditActionLabel, formatDateTime } from "../../../models/eqms";
import { detectPreviewKind, useDocDetail, type DocDetailController } from "../../../controllers/useDocDetail";
import { useUserDirectory } from "../../../controllers/useUserDirectory";
import { listProjectMembers } from "../../../controllers/accountApi";
import { UserDirError, userOptionLabel, type UserDir } from "../../components/UserCell";
import type { ProjectMember } from "../../../models/account";
// Lazy so the heavy preview libs (pdf.js / docx-preview / xlsx) code-split out of the main bundle.
const DocPreview = lazy(() => import("./DocPreview").then((m) => ({ default: m.DocPreview })));

const PIPELINE: DocStatus[] = ["DRAFT", "IN_REVIEW", "APPROVED", "RELEASED"];

// 문서 상세: the review/release pipeline heart. Metadata + AI summary on the left; status stepper,
// pending review tasks, and revision history on the right, with the action set gated by document
// status (request review / approve-reject / release / start revision / obsolete / upload revision).
export function EqmsDocumentDetailView({ eqms, currentUserId }: {
  eqms: EqmsController;
  currentUserId?: string | null;
}) {
  const [doc, setDoc] = useState<Document | null>(null);
  const [revisions, setRevisions] = useState<DocumentRevision[]>([]);
  const [reviewTasks, setReviewTasks] = useState<DocReviewTask[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [aiBusy, setAiBusy] = useState(false);
  const [translation, setTranslation] = useState<string | null>(null);
  const [transBusy, setTransBusy] = useState(false);
  const [showRequestReview, setShowRequestReview] = useState(false);
  const [showRelease, setShowRelease] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  /** A past revision opened in the preview popup (the panel below only renders the current one). */
  const [previewRev, setPreviewRev] = useState<DocumentRevision | null>(null);

  const { getToken, detailId, projectId } = eqms;

  const detail = useDocDetail(getToken, detailId, projectId);
  const userDir = useUserDirectory(getToken);

  // Resolve actor/author ids surfaced by the audit trail + comment thread to display names.
  useEffect(() => {
    userDir.ensure([
      ...detail.audit.map((a) => a.actorId),
      ...detail.comments.map((c) => c.authorId),
      ...reviewTasks.flatMap((t) => [t.assigneeId, t.requesterId]),
      ...members.map((m) => m.userId),
    ]);
  }, [detail.audit, detail.comments, reviewTasks, members, userDir]);

  // The revision shown in the preview tab: the document's current revision, else the latest upload.
  const currentRev = doc
    ? revisions.find((r) => r.id === doc.currentRevisionId) ?? revisions[revisions.length - 1] ?? null
    : null;
  const previewKind = detectPreviewKind(currentRev?.filename, doc?.format);

  useEffect(() => {
    if (detail.tab === "preview" && currentRev) void detail.ensurePreview(currentRev.id);
  }, [detail.tab, currentRev?.id]);

  async function load() {
    if (!detailId) return;
    setLoading(true);
    setError(null);
    try {
      const [d, revs, tasks] = await Promise.all([
        getDocument(getToken, detailId),
        listRevisions(getToken, detailId),
        listReviewTasks(getToken, detailId),
      ]);
      setDoc(d);
      setRevisions(revs);
      setReviewTasks(tasks);
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [getToken, detailId]);

  useEffect(() => {
    if (!projectId) return;
    listVersions(getToken, projectId).then(setVersions).catch(() => {});
    // Reviewer/approver pickers need people, not ids.
    listProjectMembers(getToken, projectId).then(setMembers).catch(() => {});
  }, [getToken, projectId]);

  if (!detailId) return null;

  async function runAction(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <button className="btn ghost" style={{ width: "auto" }} onClick={() => eqms.setDetailId(null)}>← 문서함</button>
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {loading || !doc ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <>
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
          <div style={{ flex: 2, minWidth: 320 }}>
            <div className="info-card" style={{ marginBottom: 16 }}>
              <h4>{doc.docNumber} · {doc.title}</h4>
              <div className="kv"><span>상태</span><span className="sdui-badge">{DOC_STATUS_LABEL[doc.status]}</span></div>
              <div className="kv"><span>형식</span><span>{doc.format || "-"}</span></div>
              <div className="kv"><span>대상 버전</span><span>{versions.find((v) => v.id === doc.targetVersionId)?.label ?? "없음"}</span></div>
              <div className="kv"><span>수정일</span><span>{new Date(doc.updatedAt).toLocaleString()}</span></div>
            </div>

            <div className="info-card" style={{ marginBottom: 16 }}>
              <h4>요약 / 메모</h4>
              <p style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{doc.summary || "요약 없음"}</p>
              <p style={{ whiteSpace: "pre-wrap", fontSize: 14, color: "var(--ink3)" }}>{doc.memo || "메모 없음"}</p>
            </div>

            <div className="info-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <h4 style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
                  AI 요약
                  {doc.aiSummaryAt && <span className="ai-draft" title="AI가 생성한 초안 — 사람 검토 필요">✦ 초안 · 검토 필요</span>}
                </h4>
                <div style={{ display: "flex", gap: 8 }}>
                  {doc.aiSummary && (
                    <button className="btn" style={{ width: "auto" }} disabled={transBusy} onClick={async () => {
                      if (translation) { setTranslation(null); return; }  // toggle back to original
                      setTransBusy(true); setError(null);
                      try { const r = await translateDocField(getToken, doc.id, "aiSummary", "en"); setTranslation(r.translatedText); }
                      catch (e) { setError(friendlyMessage(e)); }
                      finally { setTransBusy(false); }
                    }}>{transBusy ? "번역 중…" : translation ? "원문" : "🌐 영어 번역"}</button>
                  )}
                  <button className="btn" style={{ width: "auto" }} disabled={aiBusy} onClick={async () => {
                    setAiBusy(true); setError(null); setTranslation(null);
                    try { await generateDocAiSummary(getToken, doc.id); await load(); }
                    catch (e) { setError(friendlyMessage(e)); }
                    finally { setAiBusy(false); }
                  }}>{aiBusy ? "생성 중…" : doc.aiSummaryAt ? "✦ 재생성" : "✦ AI 요약 생성"}</button>
                </div>
              </div>
              <p style={{ whiteSpace: "pre-wrap", fontSize: 14, marginTop: 10 }}>{translation ?? (doc.aiSummary || "AI 요약이 없습니다. 생성 버튼을 눌러 초안을 만드세요.")}</p>
              {translation && <div style={{ fontSize: 12, color: "var(--blue)" }}>🌐 AI 번역(영어) · 초안</div>}
              {!translation && doc.aiKeyPoints.length > 0 && (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 14 }}>
                  {doc.aiKeyPoints.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              )}
              {doc.aiSummaryAt && (
                <div style={{ fontSize: 12, color: "var(--ink3)", marginTop: 8 }}>
                  생성 {formatDateTime(doc.aiSummaryAt)} · AI 생성물은 초안이며 규제 기록 확정 전 사람이 검토·승인해야 합니다.
                </div>
              )}
            </div>
          </div>

          <aside style={{ flex: 3, minWidth: 340, display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="info-card">
              <h4>파이프라인</h4>
              <Stepper status={doc.status} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
                {doc.status === "DRAFT" && (
                  <button className="btn primary" style={{ width: "auto" }} onClick={() => setShowRequestReview((v) => !v)}>검토요청</button>
                )}
                {doc.status === "APPROVED" && (
                  <button className="btn primary" style={{ width: "auto" }} onClick={() => setShowRelease((v) => !v)}>릴리스</button>
                )}
                {doc.status === "RELEASED" && (
                  <button className="btn primary" style={{ width: "auto" }} onClick={() => runAction(() => startDocumentRevision(getToken, doc.id))}>개정 시작</button>
                )}
                {doc.status !== "OBSOLETE" && (
                  <button className="btn ghost" style={{ width: "auto" }} onClick={() => {
                    if (window.confirm("이 문서를 폐기하시겠습니까?")) void runAction(() => obsoleteDocument(getToken, doc.id));
                  }}>폐기</button>
                )}
                {doc.status !== "OBSOLETE" && (
                  <button className="btn ghost" style={{ width: "auto" }} onClick={() => setShowUpload((v) => !v)}>리비전 업로드</button>
                )}
              </div>

              {showRequestReview && (
                <RequestReviewForm
                  members={members}
                  currentUserId={currentUserId ?? null}
                  hasRevision={!!doc.currentRevisionId}
                  userDir={userDir}
                  onCancel={() => setShowRequestReview(false)}
                  onSubmit={async (req) => {
                    await runAction(() => requestReview(getToken, doc.id, req));
                    setShowRequestReview(false);
                  }}
                />
              )}
              {showRelease && (
                <ReleaseForm
                  versions={versions}
                  onCancel={() => setShowRelease(false)}
                  onSubmit={async (versionId) => {
                    await runAction(() => releaseDocument(getToken, doc.id, versionId));
                    setShowRelease(false);
                  }}
                />
              )}
              {showUpload && (
                <UploadRevisionForm
                  getToken={getToken}
                  docId={doc.id}
                  nextLabel={nextRevLabel(revisions)}
                  onCancel={() => setShowUpload(false)}
                  onUploaded={async () => {
                    setShowUpload(false);
                    await load();
                  }}
                  onError={setError}
                />
              )}
            </div>

            <div className="info-card">
              <h4>검토 작업 · {reviewTasks.length}</h4>
              <UserDirError dir={userDir} />
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {reviewTasks.map((t) => (
                  <ReviewTaskRow
                    key={t.id}
                    task={t}
                    userDir={userDir}
                    isMine={!!currentUserId && t.assigneeId === currentUserId}
                    onAct={(action, comment) => runAction(() => actOnReview(getToken, t.id, action, comment))}
                  />
                ))}
                {reviewTasks.length === 0 && <div className="sdui-list-empty">검토 작업이 없습니다.</div>}
              </div>
            </div>

            <div className="info-card">
              <h4>리비전 이력 · {revisions.length}</h4>
              <table className="sdui-table">
                <thead><tr><th>버전</th><th>파일명</th><th>변경 요약</th><th>크기</th><th>생성일</th><th data-align="end">작업</th></tr></thead>
                <tbody>
                  {revisions.map((r) => (
                    <tr key={r.id}>
                      <td className="mono">{r.revLabel}</td>
                      <td>
                        {/* 파일명 자체가 미리보기 진입점 — 어떤 리비전이든 여기서 바로 읽는다. */}
                        <button className="filename-link" onClick={() => setPreviewRev(r)}>
                          {r.filename || `${doc.docNumber}_${r.revLabel}`}
                        </button>
                      </td>
                      <td>{r.changeSummary || "-"}</td>
                      <td>{(r.size / 1024).toFixed(1)} KB</td>
                      <td>{new Date(r.createdAt).toLocaleString()}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button
                            className="sdui-button sdui-button-secondary"
                            onClick={() => {
                              setError(null);
                              downloadRevision(getToken, doc.id, r.id, r.filename || `${doc.docNumber}_${r.revLabel}`).catch((e) => setError(friendlyMessage(e)));
                            }}
                          >
                            다운로드
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {revisions.length === 0 && <tfoot><tr><td colSpan={6} className="sdui-table-empty">리비전이 없습니다.</td></tr></tfoot>}
              </table>
            </div>

            <TracePanel detail={detail} />
          </aside>
        </div>

        <DocDetailTabs
          detail={detail}
          userDir={userDir}
          previewKind={previewKind}
          onDownloadCurrent={() => {
            if (!currentRev) return;
            setError(null);
            downloadRevision(getToken, doc.id, currentRev.id, currentRev.filename || `${doc.docNumber}_${currentRev.revLabel}`)
              .catch((e) => setError(friendlyMessage(e)));
          }}
        />

      {previewRev && (
        <DocPreviewModal
          filename={previewRev.filename || `${doc.docNumber}_${previewRev.revLabel}`}
          format={doc.format}
          subtitle={`${previewRev.revLabel}${previewRev.changeSummary ? ` · ${previewRev.changeSummary}` : ""}`}
          load={() => fetchRevisionBytes(getToken, doc.id, previewRev.id)}
          onDownload={() => {
            setError(null);
            downloadRevision(getToken, doc.id, previewRev.id,
              previewRev.filename || `${doc.docNumber}_${previewRev.revLabel}`)
              .catch((e) => setError(friendlyMessage(e)));
          }}
          onClose={() => setPreviewRev(null)}
        />
      )}
        </>
      )}
    </div>
  );
}


// The full-width tabbed section below the metadata: inline preview / audit trail / comment thread.
function DocDetailTabs({
  detail,
  userDir,
  previewKind,
  onDownloadCurrent,
}: {
  detail: DocDetailController;
  userDir: UserDir;
  previewKind: ReturnType<typeof detectPreviewKind>;
  onDownloadCurrent: () => void;
}) {
  const [commentBody, setCommentBody] = useState("");

  async function submitComment() {
    const body = commentBody.trim();
    if (!body) return;
    await detail.addComment(body);
    setCommentBody("");
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div className="tabs">
        <button className={`tab ${detail.tab === "preview" ? "active" : ""}`} onClick={() => detail.setTab("preview")}>미리보기</button>
        <button className={`tab ${detail.tab === "activity" ? "active" : ""}`} onClick={() => detail.setTab("activity")}>활동 · {detail.audit.length}</button>
        <button className={`tab ${detail.tab === "comments" ? "active" : ""}`} onClick={() => detail.setTab("comments")}>코멘트 · {detail.comments.length}</button>
      </div>

      {detail.error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {detail.error}
        </div>
      )}

      {detail.tab === "preview" && (
        <Suspense fallback={<div className="center">미리보기 로딩 중…</div>}>
          <DocPreview
            kind={previewKind}
            bytes={detail.previewBytes}
            loading={detail.previewLoading}
            error={detail.previewError}
            onDownload={onDownloadCurrent}
          />
        </Suspense>
      )}

      {detail.tab === "activity" && (
        <div className="info-card">
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {detail.audit.map((a) => (
              <div key={a.id} className="screen-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span><span className="tag">{docAuditActionLabel(a.action)}</span> <b>{userDir.label(a.actorId)}</b></span>
                  <span className="mono">{formatDateTime(a.createdAt)}</span>
                </div>
                {a.detail && <span style={{ fontSize: 13, color: "var(--ink3)" }}>{a.detail}</span>}
              </div>
            ))}
            {detail.audit.length === 0 && <div className="sdui-list-empty">활동 기록이 없습니다.</div>}
          </div>
        </div>
      )}

      {detail.tab === "comments" && (
        <div className="info-card">
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
            {detail.comments.map((c) => (
              <div key={c.id} className="screen-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <b>{userDir.label(c.authorId)}</b>
                  <span className="mono">{formatDateTime(c.createdAt)}</span>
                </div>
                <span style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{c.body}</span>
              </div>
            ))}
            {detail.comments.length === 0 && <div className="sdui-list-empty">코멘트가 없습니다.</div>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <textarea
              className="sdui-input"
              placeholder="코멘트를 입력하세요…"
              value={commentBody}
              rows={2}
              onChange={(e) => setCommentBody(e.target.value)}
              style={{ flex: 1, resize: "vertical" }}
            />
            <button className="btn primary" style={{ width: "auto" }} disabled={detail.commentBusy || !commentBody.trim()} onClick={submitComment}>
              {detail.commentBusy ? "등록 중…" : "등록"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const TRACE_LINK_LABEL: Record<string, string> = {
  DERIVES: "파생", IMPLEMENTS: "구현", VERIFIES: "검증", SATISFIES: "충족", REFERENCES: "참조", SUPERSEDES: "대체",
};

// 추적성 패널: this document's inbound/outbound trace links (요구/설계/테스트/문서), resolved to labels.
function TracePanel({ detail }: { detail: DocDetailController }) {
  const rows = [...detail.traceIn, ...detail.traceOut];
  return (
    <div className="info-card">
      <h4>추적성 · {rows.length}</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((r) => (
          <div key={r.linkId} className="screen-row" style={{ justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 13 }}>
              <span className="tag">{r.dir === "in" ? "← 입력" : "출력 →"}</span>{" "}
              <span className="tag">{TRACE_LINK_LABEL[r.linkType] ?? r.linkType}</span>{" "}
              {r.endpointType === "DOCUMENT" ? "문서" : "이슈"} · {r.endpointLabel}
            </span>
          </div>
        ))}
        {rows.length === 0 && <div className="sdui-list-empty">연결된 추적성 링크가 없습니다.</div>}
      </div>
    </div>
  );
}

function Stepper({ status }: { status: DocStatus }) {
  if (status === "OBSOLETE") {
    return <div className="tag" style={{ background: "#fdeef0" }}>폐기됨</div>;
  }
  const idx = PIPELINE.indexOf(status);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      {PIPELINE.map((s, i) => (
        <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            className="tag"
            style={{
              border: i === idx ? "1.5px solid var(--blue)" : "none",
              background: i <= idx ? "var(--blue)" : "var(--bg)",
              color: i <= idx ? "#fff" : "var(--ink2)",
            }}
          >
            {DOC_STATUS_LABEL[s]}
          </span>
          {i < PIPELINE.length - 1 && <span style={{ color: "var(--ink3)" }}>→</span>}
        </div>
      ))}
    </div>
  );
}

function ReviewTaskRow({
  task,
  userDir,
  isMine,
  onAct,
}: {
  task: DocReviewTask;
  userDir: UserDir;
  /** Whether this task is assigned to the person looking at it — only they may act on it. */
  isMine: boolean;
  onAct: (action: "approve" | "reject", comment?: string) => void;
}) {
  const [comment, setComment] = useState("");
  return (
    <div className="screen-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
        <span>
          <b>{task.role === "REVIEWER" ? "검토자" : "승인자"}</b>{" · "}
          <span title={userDir.detail(task.assigneeId)}>{userDir.label(task.assigneeId)}</span>
        </span>
        <span className="sdui-badge">{task.action === "PENDING" ? "대기 중" : task.action === "APPROVED" ? "승인" : "반려"}</span>
      </div>
      {task.dueDate && <span style={{ fontSize: 12, color: "var(--ink3)" }}>기한: {task.dueDate}</span>}
      {/* Only the assignee may act — review independence, enforced server-side (DocReviewTask.act).
          Offering 승인/반려 to anyone else, the requester included, is a control that can only fail. */}
      {task.action === "PENDING" && isMine && (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input className="sdui-input" placeholder="코멘트 (선택)" value={comment} onChange={(e) => setComment(e.target.value)} style={{ flex: 1 }} />
          <button className="btn primary" style={{ width: "auto" }} onClick={() => onAct("approve", comment)}>승인</button>
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => onAct("reject", comment)}>반려</button>
        </div>
      )}
      {task.action === "PENDING" && !isMine && (
        <span style={{ fontSize: 12, color: "var(--ink3)" }}>
          {/* 검토 ends in a vowel, 승인 in a consonant — the object particle differs (를/을). */}
          {userDir.label(task.assigneeId)} 님의 {task.role === "REVIEWER" ? "검토를" : "승인을"} 기다리는 중입니다.
        </span>
      )}
      {task.comment && task.action !== "PENDING" && <span style={{ fontSize: 13, color: "var(--ink3)" }}>{task.comment}</span>}
    </div>
  );
}

/**
 * Pick people (이름·이메일) for one review role. `taken` holds ids already committed to the OTHER
 * role — a reviewer must not also be the approver of the same document (self-approval), so those are
 * shown disabled with the reason rather than silently offered.
 */
function PeoplePicker({
  label, members, userDir, selected, taken, takenLabel, onChange,
}: {
  label: string;
  members: ProjectMember[];
  userDir: UserDir;
  selected: string[];
  taken: string[];
  takenLabel: string;
  onChange: (ids: string[]) => void;
}) {
  const available = members.filter((m) => !selected.includes(m.userId));
  const selectable = available.filter((m) => !taken.includes(m.userId));
  return (
    <div className="sdui-field">
      <span className="sdui-label">{label}</span>
      <select
        className="sdui-input"
        value=""
        disabled={selectable.length === 0}
        onChange={(e) => { if (e.target.value) onChange([...selected, e.target.value]); }}
      >
        <option value="">
          {members.length === 0 ? "선택할 수 있는 멤버가 없습니다"
            : selected.length > 0 ? `${selected.length}명 선택됨 · 더 추가…`
            : "멤버 선택…"}
        </option>
        {available.map((m) => (
          <option key={m.userId} value={m.userId} disabled={taken.includes(m.userId)}>
            {userOptionLabel(m.userId, userDir)}{taken.includes(m.userId) ? ` — 이미 ${takenLabel}` : ""}
          </option>
        ))}
      </select>
      {selected.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
          {selected.map((id) => (
            // The name is plain text, not a button: clicking a chip to inspect who it is must never
            // be the same gesture as removing them. Removal lives on its own ✕ target.
            <span key={id} className="tag" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span title={userDir.detail(id)}>{userDir.detail(id)}</span>
              <button
                type="button" className="link-btn" aria-label={`${userDir.label(id)} 제외`}
                style={{ padding: "0 2px", lineHeight: 1 }}
                onClick={() => onChange(selected.filter((x) => x !== id))}
              >✕</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function RequestReviewForm({
  members,
  currentUserId,
  hasRevision,
  userDir,
  onCancel,
  onSubmit,
}: {
  /** The project's full roster, as fetched — self-filtering happens here so the two empty states differ. */
  members: ProjectMember[];
  currentUserId: string | null;
  /** Whether the document has a revision yet — {@link Document#currentRevisionId}. */
  hasRevision: boolean;
  userDir: UserDir;
  onCancel: () => void;
  onSubmit: (req: { reviewerIds: string[]; approverIds: string[]; dueDate?: string | null }) => void;
}) {
  const [reviewerIds, setReviewerIds] = useState<string[]>([]);
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Independence of review: you cannot review or approve the document you are submitting. Mirrors
  // the server-side invariant in DocumentService.requestReview.
  const candidates = members.filter((m) => m.userId !== currentUserId);

  // Each blocked state names its own cause — "the roster failed to load", "you're the only member",
  // and "you haven't picked an approver" are three different problems with three different fixes.
  const blocked =
    // There is nothing to review until a revision exists. The server enforces this too
    // (Document.requestReview), but it only got to say so after the author had picked reviewers and
    // confirmed — and it said it in English.
    !hasRevision ? "이 문서에는 아직 리비전이 없습니다. 먼저 리비전을 업로드한 뒤 검토를 요청하세요."
    : members.length === 0 ? "프로젝트 멤버를 불러오지 못해 요청할 수 없습니다."
    : currentUserId === null ? "계정 정보를 확인하지 못해 요청할 수 없습니다. 새로고침 후 다시 시도해 주세요."
    : candidates.length === 0 ? "이 프로젝트에 다른 멤버가 없어 검토를 요청할 수 없습니다."
    : approverIds.length === 0 ? "승인자를 1명 이상 지정해야 합니다."
    : null;

  function submit() {
    onSubmit({ reviewerIds, approverIds, dueDate: dueDate || null });
  }

  // Requesting review moves the document to 검토 중 and creates tasks for named people — hard to
  // undo, and picking the wrong approver is a real use error. Make the author read the names back
  // (a summary card, not a window.confirm, so the names are actually legible).
  if (confirming) {
    return (
      <div className="info-card" style={{ marginTop: 12 }}>
        <h4>이 내용으로 검토를 요청할까요?</h4>
        <ConfirmRoleList label="검토자" ids={reviewerIds} userDir={userDir} />
        <ConfirmRoleList label="승인자" ids={approverIds} userDir={userDir} />
        <div className="kv"><span>기한</span><span>{dueDate || "없음"}</span></div>
        <p style={{ fontSize: 13, color: "var(--ink2)", margin: "8px 0 0" }}>
          요청하면 문서 상태가 <b>검토 중</b>으로 바뀌고 지정한 사람에게 검토 작업이 생성됩니다.
        </p>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="btn primary" style={{ width: "auto" }} onClick={submit}>요청 확정</button>
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => setConfirming(false)}>← 수정</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
      <PeoplePicker
        label="검토자" members={candidates} userDir={userDir}
        selected={reviewerIds} taken={approverIds} takenLabel="승인자" onChange={setReviewerIds}
      />
      <PeoplePicker
        label="승인자" members={candidates} userDir={userDir}
        selected={approverIds} taken={reviewerIds} takenLabel="검토자" onChange={setApproverIds}
      />
      <label className="sdui-field">
        <span className="sdui-label">기한 (선택)</span>
        <input className="sdui-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </label>
      {blocked && <p style={{ fontSize: 13, color: "var(--ink2)", margin: 0 }}>{blocked}</p>}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          className="btn primary" style={{ width: "auto" }}
          disabled={blocked !== null} title={blocked ?? undefined}
          onClick={() => setConfirming(true)}
        >요청</button>
        <button className="btn ghost" style={{ width: "auto" }} onClick={onCancel}>취소</button>
      </div>
    </div>
  );
}

/** One role's confirmed roster, spelled out as 이름 · 소속 · 이메일 so the author can actually check it. */
function ConfirmRoleList({ label, ids, userDir }: { label: string; ids: string[]; userDir: UserDir }) {
  return (
    <div className="kv" style={{ alignItems: "flex-start" }}>
      <span>{label} · {ids.length}</span>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "right" }}>
        {ids.length === 0 ? "없음" : ids.map((id) => <span key={id}>{userDir.detail(id)}</span>)}
      </span>
    </div>
  );
}

function ReleaseForm({
  versions,
  onCancel,
  onSubmit,
}: {
  versions: Version[];
  onCancel: () => void;
  onSubmit: (versionId: string | null) => void;
}) {
  const [versionId, setVersionId] = useState("");
  return (
    <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "flex-end" }}>
      <label className="sdui-field" style={{ flex: 1 }}>
        <span className="sdui-label">릴리스 버전 (선택)</span>
        <select className="sdui-input" value={versionId} onChange={(e) => setVersionId(e.target.value)}>
          <option value="">없음</option>
          {versions.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
        </select>
      </label>
      <button className="btn primary" style={{ width: "auto" }} onClick={() => onSubmit(versionId || null)}>릴리스</button>
      <button className="btn ghost" style={{ width: "auto" }} onClick={onCancel}>취소</button>
    </div>
  );
}

function nextRevLabel(revisions: DocumentRevision[]): string {
  if (revisions.length === 0) return "A";
  const last = revisions[revisions.length - 1]?.revLabel ?? "";
  if (/^[A-Z]$/.test(last)) {
    const next = String.fromCharCode(last.charCodeAt(0) + 1);
    if (next <= "Z") return next;
  }
  return "";
}

function UploadRevisionForm({
  getToken,
  docId,
  nextLabel,
  onCancel,
  onUploaded,
  onError,
}: {
  getToken: GetToken;
  docId: string;
  nextLabel: string;
  onCancel: () => void;
  onUploaded: () => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [revLabel, setRevLabel] = useState(nextLabel);
  const [changeSummary, setChangeSummary] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const canSubmit = revLabel.trim().length > 0 && !!file && !busy;

  async function submit() {
    if (!revLabel.trim() || !file) return;
    setBusy(true);
    try {
      await uploadRevisionFile(getToken, docId, file, revLabel.trim(), changeSummary.trim() || undefined);
      setRevLabel("");
      setChangeSummary("");
      setFile(null);
      await onUploaded();
    } catch (e) {
      onError(friendlyMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
      <label className="sdui-field">
        <span className="sdui-label">리비전 라벨</span>
        <input className="sdui-input" value={revLabel} onChange={(e) => setRevLabel(e.target.value)} />
      </label>
      <label className="sdui-field">
        <span className="sdui-label">파일</span>
        <FileDrop compact file={file} onFile={setFile} />
      </label>
      <label className="sdui-field">
        <span className="sdui-label">변경 요약 (선택)</span>
        <input className="sdui-input" value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn primary" style={{ width: "auto" }} disabled={!canSubmit} onClick={submit}>
          {busy ? "업로드 중…" : "업로드"}
        </button>
        <button className="btn ghost" style={{ width: "auto" }} onClick={onCancel} disabled={busy}>취소</button>
      </div>
    </div>
  );
}